import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { Subscribable, SubscribableOrNullableValue } from 'knockout';
import './voteBubble.html';
import './voteBubble.scss';

export interface VoteBubbleVMConfig {
    upvote?: SubscribableOrNullableValue<boolean>
    downvote?: SubscribableOrNullableValue<boolean>
    value?: SubscribableOrNullableValue<number>
    icon?: SubscribableOrNullableValue<string>
    size?: SubscribableOrNullableValue<number>

    click?: Action<[number | undefined]>
}

@injectable()
export default class VoteBubbleVM {
    private clickHandler?: Action<[number | undefined]>

    title: Subscribable<string | undefined>
    icon: Subscribable<string | undefined>

    hasTitle: Subscribable<boolean>
    hasIcon: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) config: VoteBubbleVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo
    ) {
        const isUpvoteMode = ko.flattenComputed(config.upvote, false);
        const isDownvoteMode = ko.flattenComputed(config.downvote, false);
        const isUnknownMode = ko.pureComputed(() => !isUpvoteMode() && !isDownvoteMode());

        const value = ko.flattenComputed(config.value);
        const icon = ko.flattenComputed(config.icon);

        this.title = ko.strictComputed((value, icon) => {
            if (value != undefined) {
                if (value > 0)
                    return `+${value}`;

                return value.toString();
            }
        }, [value, icon]);

        this.icon = ko.pureComputed(() => this.title() == undefined ? icon() : undefined);

        this.hasTitle = this.title.isNotNull();
        this.hasIcon = this.icon.isNotNull();

        const size = ko.flattenComputed(config.size);
        const fontSize = size.mapNotNull(size => size * 0.5);

        const isUpvote = ko.pureComputed(() => isUpvoteMode() || isUnknownMode() && (value() ?? 0) > 0);
        const isDownvote = ko.pureComputed(() => isDownvoteMode() || isUnknownMode() && (value() ?? 0) < 0);

        this.clickHandler = config.click;

        ko.applyBindingsToNode(info.element, {
            'click': () => this.clickHandler?.(value()),

            'css': {
                'vote-bubble wcc-animated zoomIn': true,
                'vote-bubble--upvote': isUpvote,
                'vote-bubble--downvote': isDownvote,
                'vote-bubble--control': this.clickHandler != undefined
            },

            'style': {
                'width': size.toPx(true),
                'height': size.toPx(true),
                'font-size': fontSize.toPx(true)
            }
        });
    }
}